import {
  buildRouteEtaSegments,
  formatDeliveryBeforeTime,
  formatEstimatedPickupTime,
  getOrderDeliveredFooter,
  isOrderPickedUp,
  isPickupDeliveryOrder,
} from '../orderManagementOrderHelpers';
import { TRACKING_STATUS } from './constants';
import { parseGeoLocationToLatLng } from '../orderManagementUtils';

function offsetRestaurantFromCustomer([lat, lng]) {
  return [lat + 0.014, lng - 0.018];
}

export function mapInternalStatusToTracking(status) {
  const s = String(status ?? '').toLowerCase();
  if (s.includes('delivered') || s.includes('picked in store')) return TRACKING_STATUS.DELIVERED;
  if (s.includes('cancel') || s.includes('damaged') || s.includes('unpicked')) {
    return TRACKING_STATUS.CANCELLED;
  }
  if (s.includes('picked the order') || s.includes('at the door')) {
    return TRACKING_STATUS.ON_DELIVERY;
  }
  if (s.includes('pickup pending')) {
    return TRACKING_STATUS.PREPARING;
  }
  return TRACKING_STATUS.PREPARING;
}

export function getProgressForStatus(status) {
  if (status === TRACKING_STATUS.DELIVERED) return 100;
  if (status === TRACKING_STATUS.ON_DELIVERY) return 68;
  if (status === TRACKING_STATUS.CANCELLED) return 0;
  return 32;
}

export function getCustomerPosition(order) {
  return (
    parseGeoLocationToLatLng(order?.customer_geolocation) ??
    parseGeoLocationToLatLng(order?.delivery_geolocation) ??
    null
  );
}

export function getRestaurantPosition(order, customerPosition) {
  const kitchen = parseGeoLocationToLatLng(order?.kitchen_geolocation);
  if (kitchen) return kitchen;
  if (customerPosition) return offsetRestaurantFromCustomer(customerPosition);
  return null;
}

export function interpolatePosition(start, end, ratio) {
  return [start[0] + (end[0] - start[0]) * ratio, start[1] + (end[1] - start[1]) * ratio];
}

export function getRiderPosition(order, status, restaurantPosition, customerPosition) {
  if (status !== TRACKING_STATUS.ON_DELIVERY || !restaurantPosition || !customerPosition) {
    return null;
  }
  const ratio = Number(order?.delivery_progress) > 0 ? Number(order.delivery_progress) / 100 : 0.58;
  return interpolatePosition(restaurantPosition, customerPosition, Math.min(0.92, Math.max(0.2, ratio)));
}

function getTrackingStatusLabel(internalStatus, { status, hasDriver, pickedUp }) {
  const s = String(internalStatus ?? '').toLowerCase();

  if (s.includes('pickup pending')) return 'Driver Pickup Pending';
  if (s.includes('picked the order')) return 'Driver Picked The Order';
  if (s.includes('at the door')) return 'Driver At The Door';
  if (s.includes('delivered') || s.includes('picked in store')) return 'Delivered';
  if (s.includes('cancel') || s.includes('damaged') || s.includes('unpicked')) return 'Cancelled';

  if (status === TRACKING_STATUS.ON_DELIVERY) {
    if (hasDriver && !pickedUp) return 'Driver Pickup Pending';
    if (pickedUp) return 'On Delivery';
  }

  if (status === TRACKING_STATUS.DELIVERED) return 'Delivered';
  if (status === TRACKING_STATUS.CANCELLED) return 'Cancelled';
  return 'Preparing';
}

export function apiOrderToTrackingOrder(order) {
  const internalStatus = order?.internal_order_status;
  const customerPosition = getCustomerPosition(order);
  const status = mapInternalStatusToTracking(internalStatus);
  const restaurantPosition = customerPosition
    ? getRestaurantPosition(order, customerPosition)
    : parseGeoLocationToLatLng(order?.kitchen_geolocation);
  const riderPosition = getRiderPosition(order, status, restaurantPosition, customerPosition);
  const customerName = `${order?.cust_firstName ?? ''} ${order?.cust_lastName ?? ''}`.trim() || 'Customer';
  const driver = order?.driver_information ?? {};
  const hasDriver = driver?.email != null || driver?.first_name != null;
  const kitchenAddress =
    order?.kitchen_geolocation?.formattedAddress ?? order?.pickup_kitchen_address ?? null;
  const customerImage = order?.customer_profile_picture ?? order?.cust_profile_picture ?? null;
  const driverImage = driver?.driver_profile_picture ?? null;
  const driverSubtitle = driver?.driver_type
    ? String(driver.driver_type).replace(/_/g, ' ')
    : null;
  const pickedUp = isOrderPickedUp(order);
  const awaitingPickup = hasDriver && !pickedUp;
  const routeEta = buildRouteEtaSegments(order);
  const routeMode = !hasDriver ? 'no_driver' : awaitingPickup ? 'awaiting_pickup' : 'picked_up';
  const statusLabel = getTrackingStatusLabel(internalStatus, { status, hasDriver, pickedUp });
  const deliveredFooter = getOrderDeliveredFooter(order);
  const isPickupOrder = isPickupDeliveryOrder(order);

  return {
    id: String(order._id ?? order.order_no),
    orderNo: String(order.order_no ?? order._id ?? '—'),
    restaurantName: order?.kitchen_name ?? order?.kitchen_managingBranchName ?? 'Restaurant',
    kitchenAddress,
    kitchenImage: null,
    customerName,
    customerImage,
    deliveryAddress:
      order?.customer_geolocation?.formattedAddress ?? order?.delivery_address ?? '—',
    status,
    statusLabel,
    progress: getProgressForStatus(status),
    restaurantPosition,
    customerPosition,
    riderPosition,
    hasMapLocation: Boolean(customerPosition),
    placedAt: order?.create_at ?? order?.order_time ?? order?.createdAt ?? null,
    updatedAt: order?.updated_at ?? order?.dispatch_sort_time ?? order?.create_at ?? null,
    raw: order,
    hasDriver,
    driverName: hasDriver
      ? [driver.first_name, driver.last_name].filter(Boolean).join(' ').trim() || '—'
      : null,
    driverImage,
    driverSubtitle,
    pickedUp,
    awaitingPickup,
    isPickupOrder,
    routeMode,
    routeEta,
    routeFirstLegComplete: routeMode === 'picked_up',
    routeSecondLegComplete: routeMode === 'picked_up' && status === TRACKING_STATUS.DELIVERED,
    routeLabel: deliveredFooter
      ? deliveredFooter.label
      : routeMode === 'no_driver'
        ? 'Delivery Before'
        : routeMode === 'awaiting_pickup'
          ? 'Estimated Pickup'
          : 'Est Delivery',
    routeTime: deliveredFooter
      ? deliveredFooter.time
      : routeMode === 'awaiting_pickup'
        ? formatEstimatedPickupTime(order, driver)
        : formatDeliveryBeforeTime(order?.delivery_time_slot),
  };
}

export function apiOrdersToTrackingOrders(orders) {
  return (orders ?? []).map(apiOrderToTrackingOrder);
}

export function getMapPlottableOrders(trackingOrders) {
  return (trackingOrders ?? []).filter((o) => o.hasMapLocation && o.customerPosition);
}

export function filterTrackingOrders(orders, { tab, search }) {
  let list = orders;
  if (tab && tab !== 'all') {
    list = list.filter((o) => o.status === tab);
  }
  const q = String(search ?? '').trim().toLowerCase();
  if (q) {
    list = list.filter(
      (o) =>
        o.orderNo.toLowerCase().includes(q) ||
        o.customerName.toLowerCase().includes(q) ||
        o.restaurantName.toLowerCase().includes(q) ||
        o.deliveryAddress.toLowerCase().includes(q)
    );
  }
  return list;
}

export function countByTab(orders) {
  return {
    all: orders.length,
    [TRACKING_STATUS.PREPARING]: orders.filter((o) => o.status === TRACKING_STATUS.PREPARING).length,
    [TRACKING_STATUS.ON_DELIVERY]: orders.filter((o) => o.status === TRACKING_STATUS.ON_DELIVERY)
      .length,
    [TRACKING_STATUS.DELIVERED]: orders.filter((o) => o.status === TRACKING_STATUS.DELIVERED).length,
  };
}
